import type { ITicketFile } from "../../types/tickets";

export type TicketDisplayStatus =
  | "Awaiting your reply"
  | "In progress"
  | "Resolved"
  | "Closed";

export const TICKET_STATUS_LABELS: TicketDisplayStatus[] = [
  "Awaiting your reply",
  "In progress",
  "Resolved",
  "Closed",
];

/** Zammad's raw state names (lowercased) mapped to the customer-facing status. */
const ZAMMAD_STATE_TO_DISPLAY_STATUS: Record<string, TicketDisplayStatus> = {
  new: "In progress",
  open: "In progress",
  "pending reminder": "Awaiting your reply",
  closed: "Closed",
  merged: "Closed",
  "pending close": "Resolved",
};

/**
 * Case-insensitive match of a ticket's `state` against the display statuses,
 * accepting either a display label or a raw Zammad state name.
 */
export const matchTicketDisplayStatus = (
  state?: string | null,
): TicketDisplayStatus | null => {
  const normalized = state?.trim().toLowerCase();
  if (!normalized) return null;
  return (
    ZAMMAD_STATE_TO_DISPLAY_STATUS[normalized] ??
    TICKET_STATUS_LABELS.find((label) => label.toLowerCase() === normalized) ??
    null
  );
};

export const isTicketStateClosed = (state?: string | null): boolean => {
  const status = matchTicketDisplayStatus(state);
  return status === "Resolved" || status === "Closed";
};

const isSameCalendarDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "2h ago" / "Yesterday" / "Sep 13" (adds the year only if it isn't the current one). */
export const formatRelativeUpdate = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";
  const now = new Date();

  if (isSameCalendarDay(date, now)) {
    const diffMins = Math.max(
      0,
      Math.floor((now.getTime() - date.getTime()) / 60000),
    );
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    return `${Math.floor(diffMins / 60)}h ago`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear()
      ? { year: "numeric" as const }
      : {}),
  });
};

/**
 * The files the support form linked from an article. Zammad attachments are not
 * included: they already come on the article itself.
 */
export const linkedFilesFor = (
  files: ITicketFile[] | undefined,
  articleId: number,
): ITicketFile[] =>
  (files ?? []).filter(
    (file) => file.source === "link" && file.article_id === articleId,
  );
